import { Button, Group, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { formatDuration, type AutofillProposal, type AutofillUnfilled } from '@reditelji/shared';
import { plDod, plVrz } from '../lib/ui';
import { useUi } from '../state/ui';
import { range, unfilledReasonText } from './derive';
import type { ExceptionKind, PlanHeadline, ReviewItem, ReviewSplit } from './planReview';

/** Accept-the-bulk button: the count carries the grammar. */
const cleanLabel = (n: number) => (n === 1 ? 'Sprejmi 1 čistega' : `Sprejmi ${n} čistih`);

/** What the review list deliberately does not show. */
function onBoardLine(n: number): string {
  if (n === 1) return '1 čist predlog je na deski.';
  if (n === 2) return '2 čista predloga sta na deski.';
  if (n === 3 || n === 4) return `${n} čisti predlogi so na deski.`;
  return `${n} čistih predlogov je na deski.`;
}

const KIND_MARK: Record<ExceptionKind, string> = {
  inexperienced: '⚑',
  'short-gap': '⚠',
  'long-day': '⚠',
  reconfirm: '✉',
};

/**
 * The plan read as an outcome: coverage it reaches, the few proposals that ask
 * for a judgement, and what stays uncovered. The clean bulk is not listed — it
 * is on the board, and one button takes it.
 */
export function PlanPanel({
  review,
  headline,
  unfilled,
  onAcceptClean,
  onAcceptMany,
  onRejectAll,
  onAcceptProposal,
  onRejectProposal,
}: {
  review: ReviewSplit;
  headline: PlanHeadline;
  unfilled: AutofillUnfilled[];
  onAcceptClean: () => void;
  onAcceptMany: (picked: AutofillProposal[]) => void;
  onRejectAll: () => void;
  onAcceptProposal: (p: AutofillProposal) => void;
  onRejectProposal: (p: AutofillProposal) => void;
}) {
  const { selectGap } = useUi();
  const total = review.exceptions.length + review.clean.length;
  // Kinds are ordered hardest-first, so a row that leads with 'reconfirm'
  // bends no rule — it only costs the person a second confirmation.
  const rules = review.exceptions.filter((e) => e.kinds[0] !== 'reconfirm');
  const soft = review.exceptions.filter((e) => e.kinds[0] === 'reconfirm');
  // A handful reads fine open; thirty rows of one shared cause do not.
  const [showSoft, setShowSoft] = useState(soft.length <= 6);
  const unfilledMin = unfilled.reduce((a, g) => a + (g.toMin - g.fromMin), 0);

  return (
    <Stack gap={8}>
      <div className="tl-plan-card">
        <Text size="sm" fw={700}>
          {plDod(total)}
        </Text>
        <div className="tl-plan-cov">
          <span>{headline.coverageNow} %</span>
          <span className="tl-plan-arrow">→</span>
          <span className="tl-plan-cov-after">{headline.coverageAfter} %</span>
          <span className="tl-plan-cov-note">pokritost oken</span>
        </div>
        <Text size="xs" c="dimmed">
          {review.utilisation.length === 1
            ? 'Delo dobi 1 prostovoljec. '
            : `Delo dobi ${review.utilisation.length} prostovoljcev. `}
          Nič ni potrjeno, dokler ne sprejmeš.
        </Text>
        <Group gap={6} mt={8}>
          <Button
            size="compact-sm"
            style={{ background: 'oklch(0.35 0.05 245)' }}
            disabled={review.clean.length === 0}
            onClick={onAcceptClean}
          >
            {cleanLabel(review.clean.length)}
          </Button>
          <Button size="compact-sm" variant="default" onClick={onRejectAll}>
            Zavrni predlog
          </Button>
        </Group>
      </div>

      {rules.length > 0 ? (
        <>
          <div className="tl-detail-eyebrow">ZA PREGLED · {rules.length}</div>
          {rules.map((item) => (
            <ExceptionRow
              key={item.key}
              item={item}
              onAccept={() => onAcceptProposal(item.proposal)}
              onReject={() => onRejectProposal(item.proposal)}
            />
          ))}
        </>
      ) : null}

      {soft.length > 0 ? (
        <>
          <div className="tl-detail-eyebrow">ZNOVA BODO POTRJEVALI · {soft.length}</div>
          <Text size="xs" c="dimmed" mb={6}>
            Brifing zanje je že odšel, zato jih sprejeta izmena vrne v „čaka“. Če si spremembo
            že dogovoril, vklopi „popravek brez ponovne potrditve“ v orodni vrstici.
          </Text>
          <Group gap={6} mb={6}>
            <Button
              size="compact-sm"
              variant="default"
              onClick={() => onAcceptMany(soft.map((item) => item.proposal))}
            >
              Sprejmi vse iz te skupine ({soft.length})
            </Button>
            <Button size="compact-sm" variant="subtle" onClick={() => setShowSoft(!showSoft)}>
              {showSoft ? 'Skrij seznam ▴' : 'Pokaži seznam ▾'}
            </Button>
          </Group>
          {showSoft
            ? soft.map((item) => (
                <ExceptionRow
                  key={item.key}
                  item={item}
                  onAccept={() => onAcceptProposal(item.proposal)}
                  onReject={() => onRejectProposal(item.proposal)}
                />
              ))
            : null}
        </>
      ) : null}

      {/* Says only what an empty exception list actually means: of the proposals
          still standing, none bends a rule. It deliberately makes no claim about
          the urgent posts — a rejected proposal and an unfilled gap both leave
          the review empty-handed, so a positive claim there would outrun what
          this list knows. The flagged ones speak for themselves above. */}
      {review.exceptions.length === 0 ? (
        <Text size="xs" c="dimmed">
          Noben predlog ne krši pravila o premoru in nobenemu ne podaljša dneva čez
          priporočenega.
        </Text>
      ) : null}

      {review.clean.length > 0 ? (
        <Text size="xs" c="dimmed">
          {onBoardLine(review.clean.length)}
        </Text>
      ) : null}

      {unfilled.length > 0 ? (
        <>
          <div className="tl-detail-eyebrow">
            OSTAJA NEPOKRITO · {plVrz(unfilled.length)} ({formatDuration(unfilledMin)})
          </div>
          {unfilled.map((g) => (
            <div
              key={`${g.postId}-${g.fromMin}`}
              className="tl-exc tl-exc--gap"
              onClick={() => selectGap({ postId: g.postId, fromMin: g.fromMin, toMin: g.toMin })}
              title="klikni za kandidate"
            >
              <div className="tl-exc-head">
                <span className="tl-exc-code">{g.postCode}</span>
                {g.urgent ? <span className="tl-urgent">NUJEN</span> : null}
                <span className="tl-exc-time">{range(g.fromMin, g.toMin)}</span>
              </div>
              <div className="tl-exc-note">{unfilledReasonText(g)}</div>
            </div>
          ))}
        </>
      ) : null}
    </Stack>
  );
}

function ExceptionRow({
  item,
  onAccept,
  onReject,
}: {
  item: ReviewItem;
  onAccept: () => void;
  onReject: () => void;
}) {
  const { focusedProposal, focusProposal } = useUi();
  const p = item.proposal;
  return (
    <div
      className={[
        'tl-exc',
        item.kinds[0] === 'reconfirm' ? 'tl-exc--soft' : '',
        focusedProposal === item.key ? 'tl-exc--focused' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => focusProposal(item.key)}
      title="klikni, da se časovnica premakne nanj"
    >
      <div className="tl-exc-head">
        <span className="tl-exc-code">{p.gap.postCode}</span>
        {p.gap.urgent ? <span className="tl-urgent">NUJEN</span> : null}
        <span className="tl-exc-name">{p.volunteerName}</span>
        <span className="tl-exc-time">{range(p.gap.fromMin, p.gap.toMin)}</span>
      </div>
      <div className="tl-exc-note">
        <span className="tl-exc-mark">{KIND_MARK[item.kinds[0]!]}</span>
        {item.note}
      </div>
      <div className="tl-exc-acts">
        <button
          type="button"
          className="tl-exc-act tl-exc-act--yes"
          aria-label={`Sprejmi predlog: ${p.volunteerName}, ${p.gap.postCode}, ${range(p.gap.fromMin, p.gap.toMin)}`}
          onClick={(e) => {
            e.stopPropagation();
            onAccept();
          }}
        >
          ✓ Sprejmi
        </button>
        <button
          type="button"
          className="tl-exc-act"
          aria-label={`Zavrni predlog: ${p.volunteerName}, ${p.gap.postCode}, ${range(p.gap.fromMin, p.gap.toMin)}`}
          onClick={(e) => {
            e.stopPropagation();
            onReject();
          }}
        >
          ✕ Zavrni
        </button>
      </div>
    </div>
  );
}
